import { Injectable, signal } from '@angular/core';
import { supabase } from '../supabase/supabase.client';

export interface DashboardStats {
  totalProperties: number;
  publishedProperties: number;
  draftProperties: number;
  totalMessages: number;
  unansweredMessages: number;
  todayMessages: number;
  totalViews: number;
  featuredProperties: number;
}

export interface PropertyTypeStats {
  type: string;
  count: number;
  percentage: number;
}

export interface ListingTypeStats {
  type: string;
  count: number;
  percentage: number;
}

export interface MessageStats {
  date: string;
  count: number;
}

interface SupabaseError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  
  constructor() {}

  /**
   * Get comprehensive dashboard statistics - OPTIMIZED with parallel queries
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Execute all queries in parallel for maximum performance
      const [propertyStats, messageStats] = await Promise.all([
        this.getPropertyStats(),
        this.getMessageStats()
      ]);

      return {
        ...propertyStats,
        ...messageStats,
        totalViews: 0 // Placeholder - would need view tracking table
      };
    } catch (error) {
      this.logError('Failed to fetch dashboard stats', error as Error);
      throw error;
    }
  }

  /**
   * Get property statistics using aggregation - optimized with select count
   */
  private async getPropertyStats(): Promise<{
    totalProperties: number;
    publishedProperties: number;
    draftProperties: number;
    featuredProperties: number;
  }> {
    try {
      // Single query with minimal fields for counting
      const { data, error, count } = await supabase
        .from('properties')
        .select('is_published, add_to_homepage', { count: 'exact' });

      if (error) {
        this.logError('Failed to fetch property stats', error);
        throw new Error(`Failed to fetch property stats: ${error.message}`);
      }

      const totalProperties = count || 0;
      const publishedProperties = data?.filter((p: any) => p.is_published).length || 0;
      const featuredProperties = data?.filter((p: any) => p.add_to_homepage).length || 0;
      const draftProperties = totalProperties - publishedProperties;

      return {
        totalProperties,
        publishedProperties,
        draftProperties,
        featuredProperties
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get message statistics - optimized query
   */
  private async getMessageStats(): Promise<{
    totalMessages: number;
    unansweredMessages: number;
    todayMessages: number;
  }> {
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      // Single query with count and minimal fields
      const { data, error, count } = await supabase
        .from('contact_messages')
        .select('created_at, is_answered', { count: 'exact' });

      if (error) {
        this.logError('Failed to fetch message stats', error);
        throw new Error(`Failed to fetch message stats: ${error.message}`);
      }

      const totalMessages = count || 0;
      const unansweredMessages = data?.filter((m: any) => !m.is_answered).length || 0;
      
      const todayMessages = data?.filter((m: any) => 
        new Date(m.created_at) >= todayStart
      ).length || 0;

      return {
        totalMessages,
        unansweredMessages,
        todayMessages
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get property distribution by type - OPTIMIZED with database aggregation
   */
  async getPropertyTypeStats(): Promise<PropertyTypeStats[]> {
    try {
      const { data, error, count } = await supabase
        .from('properties')
        .select('property_type', { count: 'exact' })
        .eq('is_published', true);

      if (error) {
        this.logError('Failed to fetch property type stats', error);
        throw new Error(`Failed to fetch property type stats: ${error.message}`);
      }

      const total = count || 0;
      if (total === 0) return [];

      // Optimized reduce with type safety
      const typeCounts = new Map<string, number>();
      data?.forEach((prop: any) => {
        const type = prop.property_type || 'Unknown';
        typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
      });

      // Convert to array with percentages
      return Array.from(typeCounts.entries())
        .map(([type, count]) => ({
          type,
          count,
          percentage: Math.round((count / total) * 100)
        }))
        .sort((a, b) => b.count - a.count);

    } catch (error) {
      this.logError('Exception fetching property type stats', error as Error);
      throw error;
    }
  }

  /**
   * Get distribution by listing type (sale/rent) - OPTIMIZED
   */
  async getListingTypeStats(): Promise<ListingTypeStats[]> {
    try {
      const { data, error, count } = await supabase
        .from('properties')
        .select('listing_type', { count: 'exact' })
        .eq('is_published', true);

      if (error) {
        this.logError('Failed to fetch listing type stats', error);
        throw new Error(`Failed to fetch listing type stats: ${error.message}`);
      }

      const total = count || 0;
      if (total === 0) return [];

      // Use Map for better performance
      const typeCounts = new Map<string, number>();
      data?.forEach((prop: any) => {
        const type = prop.listing_type || 'Unknown';
        typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
      });

      return Array.from(typeCounts.entries())
        .map(([type, count]) => ({
          type: this.capitalizeFirst(type),
          count,
          percentage: Math.round((count / total) * 100)
        }))
        .sort((a, b) => b.count - a.count);

    } catch (error) {
      this.logError('Exception fetching listing type stats', error as Error);
      throw error;
    }
  }

  /**
   * Get message trends over last 7 days - OPTIMIZED with single query
   */
  async getMessageTrends(): Promise<MessageStats[]> {
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      
      const { data, error } = await supabase
        .from('contact_messages')
        .select('created_at')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (error) {
        this.logError('Failed to fetch message trends', error);
        throw new Error(`Failed to fetch message trends: ${error.message}`);
      }

      // Initialize last 7 days with Map for better performance
      const dateCounts = new Map<string, number>();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        dateCounts.set(dateStr, 0);
      }

      // Count messages per day
      data?.forEach((msg: any) => {
        const dateStr = msg.created_at.split('T')[0];
        if (dateCounts.has(dateStr)) {
          dateCounts.set(dateStr, (dateCounts.get(dateStr) || 0) + 1);
        }
      });

      return Array.from(dateCounts.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

    } catch (error) {
      this.logError('Exception fetching message trends', error as Error);
      throw error;
    }
  }

  /**
   * Get average response time for messages (in hours) - OPTIMIZED
   */
  async getAverageResponseTime(): Promise<number> {
    try {
      // Only fetch necessary fields
      const { data, error } = await supabase
        .from('contact_messages')
        .select('created_at, updated_at')
        .eq('is_answered', true)
        .not('updated_at', 'is', null);

      if (error) {
        this.logError('Failed to calculate response time', error);
        throw new Error(`Failed to calculate response time: ${error.message}`);
      }

      if (!data || data.length === 0) return 0;

      // Optimized calculation with single pass
      let totalHours = 0;
      let validCount = 0;

      data.forEach((m: any) => {
        if (m.updated_at && m.created_at) {
          const created = new Date(m.created_at).getTime();
          const updated = new Date(m.updated_at).getTime();
          const hours = (updated - created) / (1000 * 60 * 60);
          
          if (hours >= 0) { // Only count valid response times
            totalHours += hours;
            validCount++;
          }
        }
      });

      if (validCount === 0) return 0;

      const avgHours = totalHours / validCount;
      return Math.round(avgHours * 10) / 10; // Round to 1 decimal

    } catch (error) {
      this.logError('Exception calculating response time', error as Error);
      return 0;
    }
  }

  /**
   * Get all analytics data in parallel - PERFORMANCE BOOST
   */
  async getAllAnalytics(): Promise<{
    stats: DashboardStats;
    propertyTypes: PropertyTypeStats[];
    listingTypes: ListingTypeStats[];
    messageTrends: MessageStats[];
    avgResponseTime: number;
  }> {
    try {
      const [stats, propertyTypes, listingTypes, messageTrends, avgResponseTime] = 
        await Promise.all([
          this.getDashboardStats(),
          this.getPropertyTypeStats(),
          this.getListingTypeStats(),
          this.getMessageTrends(),
          this.getAverageResponseTime()
        ]);

      return {
        stats,
        propertyTypes,
        listingTypes,
        messageTrends,
        avgResponseTime
      };
    } catch (error) {
      this.logError('Exception fetching all analytics', error as Error);
      throw error;
    }
  }

  /**
   * Helper to capitalize first letter
   */
  private capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Centralized error logging with structured context
   */
  private logError(
    message: string, 
    error: SupabaseError | Error | any, 
    context?: Record<string, any>
  ): void {
    const errorDetails: Record<string, any> = {
      message,
      timestamp: new Date().toISOString(),
      service: 'AnalyticsService',
      ...context
    };

    if (error && typeof error === 'object') {
      if ('code' in error) errorDetails['code'] = error['code'];
      if ('details' in error) errorDetails['details'] = error['details'];
      if ('hint' in error) errorDetails['hint'] = error['hint'];
      if ('message' in error) errorDetails['errorMessage'] = error['message'];
    }

    console.error('[AnalyticsService Error]', errorDetails, error);
  }
}
